<script setup lang="ts">
import {computed, inject} from 'vue';
import {SvgIcon} from '../../../svg.ts';
import type {Store} from '../../store.ts';
import {statusIcon, statusClasses, shouldShowLeaf} from './utils.ts';
import {type FileDiffStatus, FILE_DIFF_STATUS, FILE_TYPE, type FileType} from '../../types/file_tree.ts';

const store = inject<Store>('store');

const {icon, name, nameHash, headPath, status, isViewed, ty} = defineProps<{
  icon: string,
  name: string,
  nameHash: string,
  headPath: string,
  status: FileDiffStatus | null,
  isViewed?: boolean,
  ty: FileType,
}>();

const isActive = computed(() => nameHash === store.activeHash.value);
const isMuted = computed(() => status === FILE_DIFF_STATUS.UNCHANGED && !isActive.value);

const isSchematic = ty === FILE_TYPE.SCHEMATIC || ty === FILE_TYPE.PROJECT_PAGE;
const shouldShow = computed(() => shouldShowLeaf(
  status,
  nameHash,
  store?.activeHash.value ?? '',
  isSchematic && store?.diffFilterConfig.showUnchangedSheets.value === true,
));
</script>

<template>
  <a
    v-if="shouldShow"
    :title="headPath"
    :href="`#diff-${nameHash}`"
    class="file-like-row"
    :class="{
      'is-viewed': isViewed,
      'is-active': isActive,
      'is-muted': isMuted,
    }"
  >
    <SvgIcon :name="icon" />
    <span class="file-like-name">{{ name }}</span>
    <SvgIcon
      v-if="status && status !== FILE_DIFF_STATUS.UNCHANGED"
      :name="statusIcon(status)"
      class="file-like-status-icon"
      :class="statusClasses(status)"
    />
  </a>
</template>

<style scoped>
@reference "#index.css";

.file-like-row {
  @apply tw:flex tw:items-center tw:gap-1 tw:p-1 tw:text-text tw:no-underline tw:hover:bg-hover;
}

.file-like-row.is-viewed {
  @apply tw:text-text-light-3;
}

.file-like-row.is-active {
  @apply tw:text-text tw:bg-active tw:rounded-[4px];
}

.file-like-row.is-muted {
  @apply tw:opacity-60;
}

.file-like-name {
  @apply tw:text-ellipsis tw:overflow-hidden tw:whitespace-nowrap;
}

.file-like-status-icon {
  @apply tw:ml-auto;
}
</style>
